import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { DEMO_PA_SUBMISSIONS, DEMO_INTAKE_DOCUMENTS, DEMO_INTAKE_ORDERS, DEMO_IMPORT_TEMPLATE } from "./seedData";

function generateKey() {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const STAGE_BREAKPOINTS = [0, 16, 38, 58, 78, 92];
const FLOW_DURATION_MS = 30000;

export const FLOW_STAGES = [
  {
    key: "import",
    status: "Review demographics info",
    label: "Import",
    icon: "mdi-cloud-download-outline",
    color: "#2470CA",
    start: 0,
    end: 16,
  },
  {
    key: "pulling",
    status: "Review demographics info",
    label: "Pulling Data",
    icon: "mdi-database-search-outline",
    color: "#2470CA",
    start: 16,
    end: 38,
  },
  {
    key: "autocomplete",
    status: "Processing",
    label: "Autocomplete",
    icon: "mdi-form-textbox",
    color: "#5D41CF",
    start: 38,
    end: 58,
  },
  {
    key: "verification",
    status: "Review clinical info",
    label: "Verification",
    icon: "mdi-shield-check-outline",
    color: "#2470CA",
    start: 58,
    end: 78,
  },
  {
    key: "submit",
    status: "Sent to plan",
    label: "Submit to Plan",
    icon: "mdi-send-check-outline",
    color: "#3D9E4C",
    start: 78,
    end: 92,
  },
  {
    key: "approved",
    status: "Approved",
    label: "Approved",
    icon: "mdi-check-decagram",
    color: "#3D9E4C",
    start: 92,
    end: 100,
  },
];

export const useDemoStore = defineStore("demo", () => {
  const active = ref(false);
  const paSubmissions = ref([]);
  const intakeDocuments = ref([]);
  const intakeOrders = ref([]);

  const flowActive = ref(false);
  const flowOrderKey = ref(null);
  const flowProgress = ref(0);
  const flowPlaying = ref(false);
  const flowSpeed = ref(1);
  const flowStageReady = ref(false);

  let _rafId = null;
  let _lastFrameTime = null;
  let _lastStageIdx = -1;

  const flowStageIndex = computed(() => {
    const p = flowProgress.value;
    for (let i = STAGE_BREAKPOINTS.length - 1; i >= 0; i--) {
      if (p >= STAGE_BREAKPOINTS[i]) return i;
    }
    return 0;
  });

  const flowComplete = computed(() => flowProgress.value >= 100);

  const flowElapsed = computed(() => {
    return (flowProgress.value / 100) * (FLOW_DURATION_MS / 1000);
  });

  function getStageProgress(stageIdx) {
    if (stageIdx < 0 || stageIdx >= FLOW_STAGES.length) return 0;
    const stage = FLOW_STAGES[stageIdx];
    const p = flowProgress.value;
    if (p <= stage.start) return 0;
    if (p >= stage.end) return 1;
    return (p - stage.start) / (stage.end - stage.start);
  }

  const flowSubmission = computed(() => {
    if (!flowOrderKey.value) return null;
    return getSubmission(flowOrderKey.value);
  });

  function _tick(now) {
    if (!flowPlaying.value) return;
    if (_lastFrameTime === null) _lastFrameTime = now;
    const delta = (now - _lastFrameTime) * flowSpeed.value;
    _lastFrameTime = now;
    const increment = (delta / FLOW_DURATION_MS) * 100;
    const newProgress = Math.min(100, flowProgress.value + increment);

    const stageIdx = flowStageIndex.value;
    const currentStage = FLOW_STAGES[stageIdx];
    const pauseAt = currentStage.end - 0.1;
    if (newProgress >= pauseAt && stageIdx < FLOW_STAGES.length - 1) {
      flowProgress.value = pauseAt;
      flowPlaying.value = false;
      flowStageReady.value = true;
      _rafId = null;
      return;
    }

    flowProgress.value = newProgress;

    const newIdx = flowStageIndex.value;
    if (newIdx !== _lastStageIdx && flowOrderKey.value) {
      updateSubmissionStatus(flowOrderKey.value, FLOW_STAGES[newIdx].status);
      _lastStageIdx = newIdx;
    }

    if (flowProgress.value >= 100) {
      flowPlaying.value = false;
      flowStageReady.value = true;
      _rafId = null;
      return;
    }
    _rafId = requestAnimationFrame(_tick);
  }

  function _startLoop() {
    _lastFrameTime = null;
    _rafId = requestAnimationFrame(_tick);
  }

  function _stopLoop() {
    if (_rafId) {
      cancelAnimationFrame(_rafId);
      _rafId = null;
    }
  }

  function _playCurrentStage() {
    flowPlaying.value = true;
    flowStageReady.value = false;
    _startLoop();
  }

  function reset() {
    paSubmissions.value = JSON.parse(JSON.stringify(DEMO_PA_SUBMISSIONS));
    intakeDocuments.value = JSON.parse(JSON.stringify(DEMO_INTAKE_DOCUMENTS));
    intakeOrders.value = JSON.parse(JSON.stringify(DEMO_INTAKE_ORDERS));
  }

  function importSubmission(submissionKey) {
    const orderKey = generateKey();
    const newPa = {
      ...JSON.parse(JSON.stringify(DEMO_IMPORT_TEMPLATE)),
      order_key: orderKey,
      created_on: new Date().toISOString(),
      modified_on: new Date().toISOString(),
      status_modified_on: new Date().toISOString(),
    };
    newPa.order_submission.portal_submission_id = submissionKey;
    paSubmissions.value.unshift(newPa);
    return orderKey;
  }

  function startFlow() {
    stopFlow();
    const orderKey = importSubmission("CMM-DEMO-LIVE");
    flowActive.value = true;
    flowOrderKey.value = orderKey;
    flowProgress.value = 0;
    flowStageReady.value = false;
    _lastStageIdx = -1;
    _playCurrentStage();
  }

  function startFlowFromAutocomplete(existingOrderKey) {
    stopFlow();
    flowActive.value = true;
    flowOrderKey.value = existingOrderKey || importSubmission("CMM-DEMO-LIVE");
    const pullingIdx = FLOW_STAGES.findIndex((s) => s.key === "pulling");
    const startIdx = pullingIdx >= 0 ? pullingIdx : 1;
    flowProgress.value = FLOW_STAGES[startIdx].start;
    flowStageReady.value = false;
    _lastStageIdx = startIdx - 1;
    updateSubmissionStatus(flowOrderKey.value, FLOW_STAGES[startIdx].status);
    _playCurrentStage();
  }

  function stopFlow() {
    _stopLoop();
    flowActive.value = false;
    flowOrderKey.value = null;
    flowProgress.value = 0;
    flowPlaying.value = false;
    flowStageReady.value = false;
    _lastStageIdx = -1;
  }

  function nextStage() {
    if (flowComplete.value) return;
    const idx = flowStageIndex.value;
    if (idx >= FLOW_STAGES.length - 1) return;
    flowProgress.value = FLOW_STAGES[idx + 1].start;
    if (flowOrderKey.value) {
      const newIdx = flowStageIndex.value;
      updateSubmissionStatus(flowOrderKey.value, FLOW_STAGES[newIdx].status);
      _lastStageIdx = newIdx;
    }
    _playCurrentStage();
  }

  function prevStage() {
    const idx = flowStageIndex.value;
    if (idx <= 0) return;
    flowProgress.value = FLOW_STAGES[idx - 1].start;
    if (flowOrderKey.value) {
      updateSubmissionStatus(flowOrderKey.value, FLOW_STAGES[idx - 1].status);
      _lastStageIdx = idx - 1;
    }
    _playCurrentStage();
  }

  function togglePlay() {
    if (flowPlaying.value) {
      flowPlaying.value = false;
      _stopLoop();
    } else {
      if (flowProgress.value >= 100) flowProgress.value = 0;
      _playCurrentStage();
    }
  }

  function setSpeed(s) {
    flowSpeed.value = s;
  }

  function seekTo(p) {
    _stopLoop();
    flowProgress.value = Math.max(0, Math.min(100, p));
    flowPlaying.value = false;
    flowStageReady.value = false;
    if (flowOrderKey.value) {
      const idx = flowStageIndex.value;
      updateSubmissionStatus(flowOrderKey.value, FLOW_STAGES[idx].status);
      _lastStageIdx = idx;
    }
    _playCurrentStage();
  }

  function getSubmission(orderKey) {
    return paSubmissions.value.find((s) => s.order_key === orderKey) || null;
  }

  function updateSubmissionStatus(orderKey, newStatus) {
    const sub = getSubmission(orderKey);
    if (sub) {
      sub.status = newStatus;
      sub.modified_on = new Date().toISOString();
      sub.status_modified_on = new Date().toISOString();
      if (sub.order_submission) {
        sub.order_submission.status = newStatus;
      }
    }
  }

  return {
    active,
    paSubmissions,
    intakeDocuments,
    intakeOrders,
    reset,
    importSubmission,
    getSubmission,
    updateSubmissionStatus,
    flowActive,
    flowOrderKey,
    flowProgress,
    flowPlaying,
    flowSpeed,
    flowStageIndex,
    flowComplete,
    flowElapsed,
    flowStageReady,
    flowSubmission,
    getStageProgress,
    startFlow,
    startFlowFromAutocomplete,
    stopFlow,
    nextStage,
    prevStage,
    togglePlay,
    setSpeed,
    seekTo,
  };
});
